import { ApqModel } from "../../domain/ApqModel";
import { SectionProps } from "./SectionProps";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { Select } from "@ofqual/portal-components/components/Select";
import { Controller } from "react-hook-form";
import { Error } from "../common/Error";
import { Grid } from "@material-ui/core";
import { fieldLabelsAO, fieldLabelsOfq } from "../sections/fieldLabels";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { UserType } from "../../redux/types";
import { SearchField } from "@ofqual/portal-components/components/SearchField";
import { QueryParams } from "@ofqual/portal-core/core/types";
import React, { useMemo, useState } from "react";
import {
  aoService,
  apqService,
  cachedQueryClassificationService,
  cachedQueryFlagService,
  apprenticeshipSearchService,
  cachedApqStakeholderService,
} from "../../services";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { Attachment } from "../../domain/Attachment";
import { useDispatch } from "react-redux";
import { deleteSelectedAttachment } from "../../redux/actions";
import { AttachmentList } from "../common/AttachmentList";
import { ContactTypeSection } from "./ContactTypeSection";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { StakeholderType } from "../../domain/StakeholderType";

export const QueryDetails: React.FC<{
  sectionProps: SectionProps<ApqModel>;
}> = ({ sectionProps }) => {
  const {
    isCreateMode,
    control,
    errors,
    disabled,
    user: { userType, token },
    item,
    clearErrors,
    setValue,
  } = sectionProps;

  const dispatch = useDispatch();

  const [isRegulatedAoDisabled, setIsRegulatedAoDisabled] = useState(false);
  const [isOtherOrgRequired, setIsOtherOrgRequired] = useState(false);

  const mappedOrgLookupService = useMemo(
    () => ({
      get: (id: string) => aoService.get(id.toString(), { token }),
      search: (params?: QueryParams) =>
        aoService.search(params || {}, { token }),
    }),
    [token]
  );

  const [apqQueryClassificationLookups] = useLookup({
    service: cachedQueryClassificationService,
    id: "id",
    label: "value",
  });

  const [apqQueryFlagLookups] = useLookup({
    service: cachedQueryFlagService,
    id: "id",
    label: "value",
  });

  const [apqStakeholderTypeLookup] = useLookup({
    service: cachedApqStakeholderService,
    id: "id",
    label: "value",
  });

  const mappedApprenticeshipStandardLookupService = useMemo(
    () => ({
      get: (id: string) =>
        apprenticeshipSearchService.get(id.toString(), { token }),
      search: (params?: QueryParams) =>
        apprenticeshipSearchService.search(params || {}, { token }),
    }),
    [token]
  );

  const deleteAttachment = (attachment: Attachment) =>
    dispatch(deleteSelectedAttachment(attachment));

  const buildGetBlob = (blobId: string): Promise<Blob> =>
    apqService.downloadAttachment(item!.id, blobId, { token });

  return (
    <Grid container spacing={3}>
      {userType === UserType.Ofqual && isCreateMode ? (
        <>
          <Grid item xs={12}>
            <Controller
              control={control}
              rules={{ required: true }}
              name="stakeholderType"
              render={({ onChange, ...props }) => (
                <Select
                  required
                  label={fieldLabelsOfq.stakeholderType}
                  disabled={disabled}
                  datatest="stakeholder-type"
                  onChange={(e) => {
                    var type = apqStakeholderTypeLookup.filter(
                      (item) => item.id === e.target.value
                    );
                    if (type !== null) {
                      if (type[0].code === StakeholderType.RegulatedAO) {
                        setIsRegulatedAoDisabled(false);
                        setIsOtherOrgRequired(false);
                        clearErrors("nonAoOrgName");
                        setValue("nonAoOrgName", null);
                      } else if (
                        type[0].code === StakeholderType.Ifate ||
                        type[0].code === StakeholderType.Apprentice
                      ) {
                        setIsOtherOrgRequired(false);
                        setIsRegulatedAoDisabled(true);
                        clearErrors("nonAoOrgName");
                        setValue("organisationId", null);
                      } else {
                        setIsRegulatedAoDisabled(true);
                        setIsOtherOrgRequired(true);
                        setValue("organisationId", null);
                        //e.target.value = "";
                        clearErrors("organisationId");
                      }
                    }
                    onChange(e);
                  }}
                  {...props}
                >
                  {apqStakeholderTypeLookup}
                </Select>
              )}
            />
            {errors.stakeholderType && (
              <Error data-test="unit-ao-validation">
                This field is required
              </Error>
            )}
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              rules={{ required: !isRegulatedAoDisabled }}
              name="organisationId"
              as={
                <SearchField
                  searchFieldVariant="single"
                  label={
                    isRegulatedAoDisabled
                      ? fieldLabelsAO.organisationId + " (disabled)"
                      : fieldLabelsAO.organisationId
                  }
                  datatest="apq-ao"
                  service={mappedOrgLookupService}
                  disabled={isRegulatedAoDisabled}
                  readOnly={false}
                  required={!isRegulatedAoDisabled}
                />
              }
            />
            {errors.organisationId && (
              <Error data-test="unit-ao-validation">
                This field is required
              </Error>
            )}
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="nonAoOrgName"
              rules={{
                required: isRegulatedAoDisabled && isOtherOrgRequired,
                maxLength: 200,
              }}
              as={
                <TextField
                  variant="outlined"
                  label={
                    !isRegulatedAoDisabled
                      ? fieldLabelsOfq.nonAoOrgName + " (disabled)"
                      : fieldLabelsOfq.nonAoOrgName
                  }
                  data-test="org-name"
                  fullWidth
                  disabled={!isRegulatedAoDisabled}
                  required={isRegulatedAoDisabled && isOtherOrgRequired}
                />
              }
            />
            {errors.nonAoOrgName && (
              <Error data-test="unit-ao-validation">
                This field is required
              </Error>
            )}
          </Grid>
          <ContactTypeSection {...sectionProps} />
        </>
      ) : null}
      <Grid item xs={12}>
        <Controller
          control={control}
          name="queryTitle"
          rules={{ required: true, maxLength: 100 }}
          as={
            <TextField
              variant="outlined"
              label={fieldLabelsAO.title}
              data-test="title-field"
              fullWidth
              InputProps={{
                readOnly: disabled,
              }}
              required
            />
          }
        />
        {errors.queryTitle?.type === "required" && (
          <Error data-test="title-validation">This field is required</Error>
        )}
        {errors.queryTitle?.type === "maxLength" && (
          <Error>Title must be less than 100 characters</Error>
        )}
      </Grid>

      <Grid item xs={12}>
        <Controller
          control={control}
          name="queryMessage"
          rules={{ required: true }}
          render={({ onChange, value }) => (
            <>
              <label>
                {fieldLabelsAO.query} {disabled ? "(Read Only)" : ""} *
              </label>
              <MarkdownEditor
                onChange={onChange}
                value={value}
                readOnly={disabled}
                dataTest="query-field"
              />
            </>
          )}
        />
        {errors.queryMessage?.type === "required" && (
          <Error data-test="query-validation">This field is required</Error>
        )}
      </Grid>

      <Grid item xs={12}>
        <Controller
          control={control}
          name="apprenticeshipStandardId"
          rules={{ required: true }}
          render={({ onChange, ...props }) => (
            <SearchField
              label={fieldLabelsAO.apprenticeshipStandard}
              disabled={disabled}
              datatest="apprenticeshipStandard-field"
              searchFieldVariant="single"
              service={mappedApprenticeshipStandardLookupService}
              required
              onChange={(e) => {
                if (e.target.value !== null) {
                  clearErrors("apprenticeshipStandardId");
                }
                onChange(e);
              }}
              {...props}
            ></SearchField>
          )}
        />
        {errors.apprenticeshipStandardId?.type === "required" && (
          <Error data-test="apprenticeshipStandard-validation">
            This field is required
          </Error>
        )}
      </Grid>

      <Grid item xs={12}>
        <Controller
          control={control}
          name="assessmentPlanVersion"
          rules={{
            min: {
              value: 0,
              message: "Entered value must be 0 or more",
            },
            valueAsNumber: true,
            pattern: {
              value: /^[0-9]+$/,
              message: "Entered value is not a whole number",
            },
          }}
          as={
            <TextField
              label={fieldLabelsAO.assessmentPlanVersionNumber}
              disabled={disabled}
              data-test="assessmentPlanVersion-field"
            ></TextField>
          }
        />
        {(errors.assessmentPlanVersion?.type === "pattern" ||
          errors.assessmentPlanVersion?.type === "min") && (
          <Error data-test="assessmentPlanVersion-validation">
            {errors.assessmentPlanVersion.message}
          </Error>
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="classificationId"
          as={
            <Select
              label={fieldLabelsAO.queryClassification}
              disabled={disabled}
              datatest="classificationId-field"
            >
              {apqQueryClassificationLookups}
            </Select>
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="queryFlag"
          as={
            <Select
              selectVariant="multiple"
              label={fieldLabelsAO.queryFlag}
              disabled={disabled}
              datatest="queryFlag-field"
            >
              {apqQueryFlagLookups}
            </Select>
          }
        />
      </Grid>
      {!isCreateMode && (
        <>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="files"
              defaultValue={[]}
              as={
                <FileInput
                  multiple={true}
                  dataTest="select-files-button"
                  disabled={disabled}
                />
              }
            />
          </Grid>
          {item?.apqAttachments ? (
            <AttachmentList
              canEdit={!disabled}
              attachments={item.apqAttachments}
              deleteAttachment={deleteAttachment}
              getBlob={buildGetBlob}
            />
          ) : null}
        </>
      )}
    </Grid>
  );
};
